import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";
import { v3 } from "oc-threejs/short3";
import { getMMR } from "../RichMetadata";
import { MUMProfil } from "../../metamodell";
import { BestelllistenEintrag, StücklistenIterator } from "./Stückliste";
import { Arrangement, ghostPool } from "./Arrangement";
import { getUMProfilModel, isLongAnschlag } from "./pfostenFunctions";
import { Box3 } from "three";
import { Ecke } from "./Ecke";
import { Segment } from "./Segment";
import { Pfosten3d } from "../arrangement3d/Pfosten3d";
import { AbstractPart3d } from "../arrangement3d/AbstractPart3d";

/**
 * Achtung, auch ein Pfosten kann ein UMProfil sein - falls ein UMProfil statt eines Pfostens verwendet
 * wird (an Mauer). Diese Klasse wird nur verwendet, wenn zwischen Element und Pfosten
 * ein UMProfil gesetzt wird.
 */
export class UMProfil extends Jaco {
  static CLASS_NAME = "UMProfil";

  isUMProfil = true;
  declare parent: Segment;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  // nix

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  // meta kann aus der Tabelle Pfosten oder UMProfil sein
  meta: MUMProfil = null;
  gesuchtePfostenHöhe = 0;
  // nachbarn können auch Mauern oder Empty sein
  nachbarLinks: any = null;
  nachbarRechts: any = null;
  model: ModelGhost = null;
  box = new Box3();
  bestellliste: BestelllistenEintrag[] = [];
  gesamtpreis = 0;
  size = v3();
  gapLinks = 0;
  gapRechts = 0;
  elemente: Element[] = [];
  ecke: Ecke;

  hovered = false;

  constructor(parent) {
    super(parent);
  }

  traverse(iterator: (jaco: Jaco) => void) {
    super.traverse(iterator);
    if (this.model) this.model.traverse(iterator);
  }

  get arrangement(): Arrangement {
    return super.arrangement as any;
  }

  createJaco3d() {
    return new AbstractPart3d(this);
  }

  update3d() {
    super.update3d();
    this.model = ghostPool(this).clearInstance(this.model);
    const gesuchteUMProfilHöhe =
      this.gesuchtePfostenHöhe + APP.settings.delta_umprofil_höhenversatz;
    const { model, box } = getUMProfilModel(
      this,
      gesuchteUMProfilHöhe,
      this.meta,
      this.nachbarLinks?.isElement
    );
    this.model = model;
    this.box.copy(box);
    box.getSize(this.size);
    // Anschlagprofil: Höhe relativ zum Tor positionieren (analog zu Pfosten.modelUpdateAsUMProfil)
    if (this.meta?.Typ === "Anschlagprofil") {
      const element = [this.nachbarLinks, this.nachbarRechts].find(
        (n) => n?.isElement && n?.getAnschlagWinkel?.()
      );
      const anschlagWinkel = element?.getAnschlagWinkel?.();
      if (anschlagWinkel) {
        this.model.position.y = isLongAnschlag(this.meta)
          ? this.meta.PositionY
          : element.position.y + anschlagWinkel.position.y + this.meta.PositionY;
      }
    }
    model.update3d();
  }

  reset() {
    super.resetCoordinates();
    this.elemente = [];
    if (this.ecke != null) {
      this.ecke.pfosten = null;
    }
    this.ecke = null;
    this.model = null;
  }

  get segment() {
    return this.parent?.isSegment ? this.parent : null;
  }

  getBox() {
    return this.box.clone();
  }

  get breite() {
    return this.size.x;
  }

  get snap() {
    const nachbarn = this.getNachbarn();
    const pfosten = nachbarn.find((n) => n.isPfosten);
    const element = nachbarn.find((n) => n.isElement);
    // Sonderfall freier Endpfosten: Hängt der Pfosten an genau einem Element (Leerraum bzw.
    // Segmentende auf der anderen Seite), muss das UMProfil zum Element hin snappen, damit die
    // Kette Element -> UMProfil -> Pfosten den korrekten Abstand hält. Andernfalls (über
    // den ersten Nicht-UMProfil-Nachbarn) erbte ein rechtsseitiges UMProfil den snap des Elements
    // (=0), snappte also gar nicht, und der Endpfosten stand 0,5 cm zu nah ("Lücke von -0,5 cm").
    // Bei Pfosten zwischen zwei Elementen (length != 1), an Ecken oder Toren bleibt das
    // Originalverhalten erhalten, da dort sonst konkurrierende Snaps entstehen.
    if (pfosten && !pfosten.isPfostenAnEcke && pfosten.elemente?.length === 1 && element) {
      return this.nachbarRechts?.isElement ? 1 : -1;
    }
    const leftNeighbour = nachbarn.filter((n) => !n.isUMProfil)[0];
    return leftNeighbour?.isPfostenAnEcke ? -1 : leftNeighbour?.snap ?? 1;
  }

  set snap(v) {
    // nothin..
  }

  getNachbarn() {
    return [this.nachbarLinks, this.nachbarRechts].filter((n) => n != null);
  }

  clone() {
    const clone = new UMProfil(this.parent);
    clone.copy(this);
    return clone;
  }

  copy(other: UMProfil) {
    super.copy(other);

    // das hier müssen exakt die persistierten oder session Werte sein!

    // zusätzlich solche, die gecloned werden müssen
    this.meta = other.meta;
    this.size.copy(other.size);

    return this;
  }

  toJSON() {
    const json = super.toJSON();
    json.type = (this.constructor as any).CLASS_NAME;
    json.meta = this.meta.Key;
    return json;
  }

  fromJSON(json: any = {}) {
    super.fromJSON(json);
    this.meta = APP.richMetadata.findAnyObjekt(json.meta);
    return this;
  }

  getMeta() {
    return this.meta;
  }

  getBoxUnclipped() {
    return APP.modelsHelper.getBoundingBox(getMMR(this.getMeta()))?.clone();
  }

  /**
   * @param iterator {StücklistenIterator}
   */
  collectArtikel(iterator: StücklistenIterator) {
    // Ordne das UMProfil der Gruppe des benachbarten Elementes zu,
    // damit es in der Baugruppe des Elements erscheint
    const elementNachbar = [this.nachbarLinks, this.nachbarRechts].find(
      (n) => (n as any)?.isElement
    ) as any;
    const ObjectGroupTag = elementNachbar?.ObjectGroupTag;
    iterator({ ObjektMeta: this.meta, ObjectGroupTag, JacoId: this.id });
  }
}
